// Portal login-loop breaker.
//
// avatar-web boots, probes the session, and on "no session" redirects to the
// portal. Normally that's a one-time bounce: a logged-out user lands on the
// portal login form and stops. But when the portal still believes the user is
// authenticated (its own session outlived the shared `.avataros.xyz` cookie),
// it auto-redirects straight back — and we bounce to login again, forever,
// with no form ever shown. See docs / the ENG auth-loop investigation.
//
// This tracks consecutive failed-auth boots within a short window and escalates:
//   1st failure  -> 'login'    normal: send to the portal login
//   2nd failure  -> 'recover'  we came back still unauthenticated => the portal
//                              bounced us; force a logout to clear its stale
//                              session (a valid login form needs no session)
//   3rd failure  -> 'login'    post-logout the portal has no session, so this
//                              login redirect finally shows the form
//   4th+         -> 'stop'     even a clean logout+login round-trip failed;
//                              stop redirecting and surface a loud error
//
// A successful session (or a gap longer than the window) resets the sequence,
// so a stale counter from an earlier visit can't strand a returning user.

const KEY = 'aos-auth-bounce'
export const AUTH_BOUNCE_WINDOW_MS = 30_000

function read(storage) {
  try {
    const parsed = JSON.parse(storage.getItem(KEY))
    if (parsed && typeof parsed.count === 'number' && typeof parsed.ts === 'number') return parsed
  } catch { /* absent or malformed — treat as fresh */ }
  return null
}

// Records one failed-auth boot and returns what the caller should do next:
// 'login' | 'recover' | 'stop'. `now`/`storage` are injectable for testing.
export function nextAuthAction(now = Date.now(), storage = sessionStorage) {
  const prev = read(storage)
  const count = prev && now - prev.ts <= AUTH_BOUNCE_WINDOW_MS ? prev.count + 1 : 1
  storage.setItem(KEY, JSON.stringify({ count, ts: now }))
  if (count === 1 || count === 3) return 'login'
  if (count === 2) return 'recover'
  return 'stop'
}

export function clearAuthBounce(storage = sessionStorage) {
  try { storage.removeItem(KEY) } catch { /* storage unavailable — nothing to clear */ }
}
